// src/pages/Employees/EmployeeDetails.tsx
import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { getProfileById, updateProfile } from "../../services/profileService";
import { Icon } from "@iconify/react";
import Modal from "../../components/Modal";
import { deleteProfile } from "../../services/profileService";
import Button from "../../components/Button";
import { toast } from "react-hot-toast"; // optional for success/error messages
import FormBuilder, { FieldConfig } from "../../components/FormBuilder";
import { employeeFields } from "./formFields";
import { flattenProfile } from "../../services/mappers";
import AddressBankDetails from "../../components/ProfileDatas/AddressBankDetails";
import { objectToFormData } from "../../utils/objectToFormData";

type TabKey = "personal" | "address";

export default function EmployeeDetails() {
  const location = useLocation();
  const navigate = useNavigate();
  const id = location.state?.id;
  const [menuOpen, setMenuOpen] = useState(false);
  const [employee, setEmployee] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const menuRef = useRef<HTMLDivElement>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string[]> | null>(
    null,
  );
  const [editingEmployee, setEditingEmployee] = useState<any>(null);
  // Tabs
  const [activeTab, setActiveTab] = useState<TabKey>("personal");

  const editableFields = [
    "email",
    "mobile_number",
    "first_name",
    "last_name",
    "date_of_birth",
    "gender",
    "marital_status",
    "father_name",
    "mother_name",
    "spouse_name",
    "aadhaar_number",
    "pan_number",
    "driving_license",
    "voter_id",
    "passport_number",
    "salary",
    "employment_type",
    "employer_name",
    "work_experience",
    "credit_score",
    "monthly_income",
    "other_liabilities",
    "status",
    "profile_photo",
    "sign_photo",
    "reference_photo",
    "profile_photo_url",
    "sign_photo_url",
    "reference_photo_url",
  ];

  // small reusable Tab button
  function TabButton({ tabKey, label }: { tabKey: TabKey; label: string }) {
    const active = activeTab === tabKey;
    return (
      <button
        onClick={() => setActiveTab(tabKey)}
        className={`relative px-4 py-2 font-medium text-sm transition-colors duration-200 cursor-pointer text-nowrap
        ${active ? "text-color " : "text-gray-400 hover:text-indigo-600"}
      `}
      >
        {label}
        {active && (
          <span className="absolute left-0 right-0 -bottom-0 h-[2px] main-bg-color rounded-full"></span>
        )}
      </button>
    );
  }

  const handleEditClick = async () => {
    if (!employee) return;

    // Build the initial edit payload from existing Employee data
    try {
      const localFlattened =
        employee.profile_id || employee.login
          ? flattenProfile(employee) // keep same shape your FormBuilder expects
          : employee;
      const merged = {
        ...localFlattened,
        loans: employee.loans ?? localFlattened.loans ?? [],
        address_bank_details:
          employee.address_bank_details ??
          localFlattened.address_bank_details ??
          [],
        login: employee.login ?? localFlattened.login ?? {},
      };

      setEditingEmployee(merged);
      setFormErrors(null);
      setEditModalOpen(true);
    } catch (err) {
      console.error("Failed to prepare edit data", err);
      toast.error("Failed to prepare edit form");
    }
  };

  const fetchEmployee = async () => {
    try {
      const res = await getProfileById(id);
      setEmployee(res.profile);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!id) {
      navigate("/employees");
      return;
    }
    fetchEmployee();
  }, [id, navigate]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }

    if (menuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [menuOpen]);

  if (loading)
    return (
      <div className="flex justify-center items-center py-12">
        <span className="animate-spin mr-2">
          <svg className="h-6 w-6 text-sky-500" fill="none" viewBox="0 0 24 24">
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8v8z"
            ></path>
          </svg>
        </span>
        <span className="text-slate-500">Loading...</span>
      </div>
    );
  if (!employee) return <div>Employee not found</div>;

  const login = employee.login;

  const handleDeleteClick = () => {
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!id) return;
    setDeleting(true);
    try {
      const res = await deleteProfile(id);
      toast.success(res.message || "Profile deleted successfully!");
      setDeleteModalOpen(false);
      navigate("/employees"); // go back to list after deletion
    } catch (err: any) {
      console.error(err);
      toast.error(err?.message || "Failed to delete Employee");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className=" mx-auto ">
      {/* Header */}
      <div className="flex items-center justify-between main-bg light-bg-color rounded-b-md   sticky top-0 p-2 mb-4 z-10">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 px-3 py-1 rounded-xl bg-white hover:bg-gray-200 text-gray-700 font-medium shadow-sm transition-all duration-150 cursor-pointer"
          >
            <Icon icon="mdi:arrow-left" width={18} />
            <span className="hidden md:block " ref={menuRef}>
              Back
            </span>
          </button>

          <h3 className="text-lg font-semibold text-color ">
            Employee Details
          </h3>
        </div>

        {/* Desktop Buttons */}
        <div className="hidden md:flex gap-3 items-center">
          <button
            onClick={handleEditClick}
            className="flex-1 inline-flex items-center justify-center gap-2  px-3 py-1 bg-white rounded-lg border border-slate-200 cursor-pointer"
          >
            <Icon icon="mdi:pencil" width={16} />
            Edit
          </button>
          <button
            onClick={handleDeleteClick}
            className="flex-1 inline-flex items-center justify-center gap-2  px-3 py-1 bg-white rounded-lg border border-rose-200 text-rose-600 cursor-pointer"
          >
            <Icon icon="mdi:delete" width={16} />
            Delete
          </button>
        </div>

        {/* Mobile Three-Dot Menu */}
        <div className="md:hidden relative" ref={menuRef}>
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className=" rounded-full hover:bg-gray-200 transition"
          >
            <Icon icon="mdi:dots-vertical" width={24} />
          </button>

          {menuOpen && (
            <div className="absolute right-0 mt-2 w-32 bg-white border rounded-lg shadow-lg z-50">
              <button
                onClick={handleEditClick}
                className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center gap-2 "
              >
                <Icon icon="mdi:pencil" width={16} />
                Edit
              </button>
              <button
                onClick={handleDeleteClick}
                className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center gap-2 text-rose-600"
              >
                <Icon icon="mdi:delete" width={16} />
                Delete
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="px-2 ">
        <div className="flex flex-col md:flex-row items-center gap-4 mb-6  light-bg-color  p-4 rounded-xl shadow col-span-2 md:col-span-2">
          {employee.profile_photo_url ? (
            <img
              src={
                employee.profile_photo_url
                  ? `${employee.profile_photo_url}`
                  : "/assets/avatar.png"
              }
              alt={employee.first_name}
              className="w-24 h-24 md:w-32 md:h-32 rounded-full object-cover"
            />
          ) : (
            <div className="w-24 h-24 md:w-32 md:h-32 rounded-full flex items-center justify-center bg-blue-500">
              <span className="text-white font-semibold text-3xl">
                {(employee.first_name?.charAt(0) ?? "?").toUpperCase()}
              </span>
            </div>
          )}
          <div className="flex-1 text-center md:text-left">
            <h2 className="text-2xl font-bold text-color ">
              {employee.first_name} {employee.last_name}
            </h2>
            <p className="text-gray-400">{employee.employee_code}</p>
            <p className="text-color ">
              <strong></strong> {employee?.email ?? "—"}
            </p>
            <div className="mt-2 flex flex-col md:flex-row md:items-center gap-4 ">
              <p
                className={`p-1 px-3  rounded-full  font-medium ${employee.status === "Active" ? "text-green-800 bg-green-100" : "text-red-500 bg-red-100"}`}
              >
                {employee.status}
              </p>
              <p className="p-1 px-3 bg-blue-100 rounded-full text-blue-800 font-medium">
                {login?.role ?? "—"}
              </p>
            </div>
          </div>
        </div>

        {/* Tabs header */}
        <div className=" px-2 mb-3 border-b border-indigo-200  overflow-x-auto flex ">
          <div className="flex overflow-x-auto">
            {/* <TabButton tabKey="loans" label="Loans" /> */}
            <TabButton tabKey="personal" label="Personal Info" />
            <TabButton tabKey="address" label="Address" />
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-3">
          {/* PERSONAL TAB */}
          {activeTab === "personal" && (
            <>
              {/* Personal Details */}
              <div className="bg-white p-4 rounded-xl shadow mb-4 col-span-2 md:col-span-1 ">
                <h3 className="text-xl font-semibold pb-3 mb-2 gap-3 border-b border-indigo-200 main-color">
                  Personal Details
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <p>
                    <strong>Mobile No:</strong> {employee?.mobile_number ?? "—"}
                  </p>
                  <p>
                    <strong>Date of Birth:</strong>{" "}
                    {employee.date_of_birth ?? "—"}
                  </p>
                  <p>
                    <strong>Gender:</strong> {employee.gender ?? "—"}
                  </p>
                  <p>
                    <strong>Marital Status:</strong>{" "}
                    {employee.marital_status ?? "—"}
                  </p>
                  <p>
                    <strong>Father Name:</strong> {employee.father_name ?? "—"}
                  </p>
                  <p>
                    <strong>Mother Name:</strong> {employee.mother_name ?? "—"}
                  </p>
                  <p>
                    <strong>Spouse Name:</strong> {employee.spouse_name ?? "—"}
                  </p>
                  <p>
                    <strong>Aadhaar:</strong> {employee.aadhaar_number ?? "—"}
                  </p>
                  <p>
                    <strong>PAN:</strong> {employee.pan_number ?? "—"}
                  </p>
                  <p>
                    <strong>Driving License:</strong>{" "}
                    {employee.driving_license ?? "—"}
                  </p>
                  <p>
                    <strong>Voter ID:</strong> {employee.voter_id ?? "—"}
                  </p>
                  <p>
                    <strong>Passport:</strong> {employee.passport_number ?? "—"}
                  </p>
                </div>
              </div>

              {/* Employment & Finance Details */}
              <div className="bg-white p-4 rounded-xl shadow mb-4 col-span-2 md:col-span-1">
                <h3 className="text-xl font-semibold pb-3 mb-2 gap-3 border-b border-gray-200 main-color">
                  Employment & Finance
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <p>
                    <strong>Employment Type:</strong>{" "}
                    {employee.employment_type ?? "—"}
                  </p>
                  {/* <p><strong>Employer Name:</strong> {employee.employer_name ?? "—"}</p> */}
                  <p>
                    <strong>Work Experience (years):</strong>{" "}
                    {employee.work_experience ?? "—"}
                  </p>
                  {/* <p><strong>Salary:</strong> {employee.salary ?? "—"}</p> */}
                  <p>
                    <strong>Monthly Income:</strong>{" "}
                    {employee.monthly_income ?? "—"}
                  </p>
                  <p>
                    <strong>Other Liabilities:</strong>{" "}
                    {employee.other_liabilities ?? "—"}
                  </p>
                  <p>
                    <strong>Credit Score:</strong>{" "}
                    {employee.credit_score ?? "—"}
                  </p>
                </div>
              </div>

              {/* Personal Details */}
              <div className="bg-white p-4 rounded-xl shadow col-span-2 md:col-span-1 ">
                <h3 className="text-xl font-semibold pb-3 mb-2 gap-3 border-b border-gray-200 main-color">
                  Reference Details
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <p>
                    <strong>Reference Name</strong>{" "}
                    {employee?.reference_name ?? "—"}
                  </p>
                  <p>
                    <strong>Reference Mobile Number:</strong>{" "}
                    {employee.reference_mobile_number ?? "—"}
                  </p>
                  <p>
                    <strong>Reference Aadhaar Number:</strong>{" "}
                    {employee.reference_aadhaar_number ?? "—"}
                  </p>
                  {employee.reference_photo_url ? (
                    <img
                      src={`${employee.reference_photo_url}`}
                      alt="Reference Photo"
                      className=" h-16 object-contain"
                    />
                  ) : (
                    <div className="h-16 flex items-center justify-center border-2 border-dashed border-gray-300 rounded text-gray-400 font-medium">
                      No Image Available
                    </div>
                  )}
                </div>
              </div>

              {/* Signature*/}

              <div className="bg-white p-4 rounded-xl shadow flex flex-col items-center col-span-2 md:col-span-1">
                <h3 className="text-lg font-semibold mb-2 border-b border-gray-200 main-color">
                  Signature
                </h3>

                {employee.sign_photo_url ? (
                  <img
                    src={`${employee.sign_photo_url}`}
                    alt="Signature"
                    className="w-48 h-16 object-contain"
                  />
                ) : (
                  <div className="w-48 h-16 flex items-center justify-center border-2 border-dashed border-gray-300 rounded text-gray-400 font-medium">
                    No Signature Available
                  </div>
                )}
              </div>
            </>
          )}

          {/* ADDRESS TAB */}
          {activeTab === "address" && (
            <div className="col-span-2">
              <AddressBankDetails
                profileId={employee.profile_id ?? employee.id} // pass whichever ID your API expects
                addressBank={employee.address_bank_details ?? []}
                onChange={(newList) =>
                  setEmployee((prev: any) => ({
                    ...prev,
                    address_bank_details: newList,
                  }))
                }
              />
            </div>
          )}
        </div>

        <Modal
          isOpen={editModalOpen}
          onClose={() => setEditModalOpen(false)}
          title="Edit Employee"
        >
          <FormBuilder
            defaultValues={editingEmployee}
            fields={employeeFields
              .filter(
                (field): field is Extract<FieldConfig, { name: string }> =>
                  "name" in field,
              ) // narrow
              .filter(
                (field) =>
                  editableFields.includes(field.name) ||
                  field.name === "username",
              )
              .map((field) =>
                field.name === "username"
                  ? { ...field, readOnly: true }
                  : field,
              )}
            onSubmit={async (formData) => {
              if (!employee) return;

              try {
                const idToUse = employee.profile_id ?? employee.id;

                // Only send editable fields
                const filtered: any = {};
                editableFields.forEach((key) => {
                  if (formData[key] !== undefined) {
                    filtered[key] = formData[key];
                  }
                });

                // 🔑 REMOVE image fields if they are NOT files
                ["profile_photo", "sign_photo", "reference_photo"].forEach(
                  (imgKey) => {
                    const v = filtered[imgKey];

                    const isFile =
                      v instanceof File ||
                      (v instanceof FileList && v.length > 0);

                    if (!isFile) {
                      delete filtered[imgKey]; // ✅ THIS FIXES EDIT
                    }
                  },
                );

                const fd = objectToFormData(filtered);

                const res = await updateProfile(idToUse, fd);

                toast.success(res.message || "Customer updated successfully!");
                setEditModalOpen(false);
                fetchEmployee();
              } catch (err: any) {
                const errorData = err?.response?.data;
                setFormErrors(errorData?.errors ?? null);
                toast.error(errorData?.message || "Failed to update customer");
              }
            }}
            onCancel={() => setEditModalOpen(false)}
            error={formErrors}
            submitLabel="Update Employee"
          />
        </Modal>

        <Modal
          isOpen={deleteModalOpen}
          onClose={() => setDeleteModalOpen(false)}
          title="Confirm Delete"
        >
          <p className="mb-5 text-gray-700">
            Are you sure you want to delete{" "}
            <span className="font-semibold">
              {employee?.first_name} {employee?.last_name}
            </span>
            ?
          </p>
          <div className="flex justify-end gap-3">
            <Button
              variant="secondary"
              onClick={() => setDeleteModalOpen(false)}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={confirmDelete}
              disabled={deleting}
            >
              {deleting ? "Deleting..." : "Delete"}
            </Button>
          </div>
        </Modal>
      </div>
    </div>
  );
}
